import Form from '@rjsf/core'
import { fields, templates, widgets } from '../ui-schema-components'
import validator from '@rjsf/validator-ajv8'
import {
  CheckIcon,
  CreateMatrix,
  Matrix,
  ModalDialog,
  Mtds,
  useCreateMatrixMutation,
  useErrorToast
} from '../../index'
import type { RJSFSchema } from '@rjsf/utils'
import { useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'

const matrixSchema: RJSFSchema = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      title: 'Navn'
    },
    code: {
      type: 'string',
      title: 'SSD2-kode'
    }
  },
  required: ['name', 'code']
}

type CreateMatrixModalProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  onCreated?: (matrix: Matrix) => void
}

export const CreateMatrixModal = ({
  isOpen,
  setOpen,
  onCreated
}: CreateMatrixModalProps) => {
  const createMatrix = useCreateMatrixMutation()
  const queryClient = useQueryClient()
  const currentDataRef = useRef<CreateMatrix | undefined>(undefined)
  const showError = useErrorToast('opprette matrisen')
  const onSubmit = async (
    formData: CreateMatrix | undefined
  ): Promise<void> => {
    if (formData != null) {
      try {
        const created = await createMatrix.mutateAsync(formData)
        if (onCreated) {
          await queryClient.refetchQueries({ queryKey: ['batch'] })
          onCreated(created)
        }
        Mtds.toast.success('Matrisen er lagt til')
        setOpen(false)
      } catch (error) {
        showError(error)
      }
    }
  }
  return (
    <ModalDialog
      id={'create-matrix-modal'}
      open={isOpen}
      onClose={(e) => {
        e.stopPropagation()
        setOpen(false)
      }}
    >
      <Mtds.Button
        command='close'
        commandfor={'create-matrix-modal'}
        aria-label={'Lukk'}
      />
      <Mtds.Prose>
        <Mtds.Heading>Legg til matrise</Mtds.Heading>
        <Form
          tagName={'div'}
          noHtml5Validate
          schema={matrixSchema}
          widgets={widgets}
          fields={fields}
          templates={templates}
          showErrorList={false}
          validator={validator}
          onChange={(e) => {
            currentDataRef.current = e.formData as CreateMatrix | undefined
          }}
        >
          <Mtds.Flex className='mt-8'>
            <Mtds.Button type='button' onClick={() => setOpen(false)}>
              Avbryt
            </Mtds.Button>
            <Mtds.Button
              data-variant='primary'
              type='button'
              onClick={async () => onSubmit(currentDataRef.current)}
            >
              <CheckIcon />
              Lagre
            </Mtds.Button>
          </Mtds.Flex>
        </Form>
      </Mtds.Prose>
    </ModalDialog>
  )
}
